/**
 * Maps ISO 3166-1 alpha-2 country codes to their most commonly spoken languages.
 * Each country is mapped to an array of language codes in priority order (most spoken first).
 * Language codes follow ISO 639-1 standard.
 */
export const COUNTRY_LANGUAGE_MAP: Record<string, string[]> = {
  // English-speaking countries
  US: ['en'], // United States
  GB: ['en'], // United Kingdom
  CA: ['en', 'fr'], // Canada
  AU: ['en'], // Australia
  NZ: ['en'], // New Zealand
  IE: ['en'], // Ireland
  ZA: ['en'], // South Africa
  SG: ['en'], // Singapore
  IN: ['hi', 'en', 'bn', 'te', 'mr', 'ta', 'ur', 'gu', 'kn', 'ml', 'or', 'pa'], // India
  PK: ['ur', 'en', 'pa', 'sd', 'ps', 'bal'], // Pakistan
  NG: ['en'], // Nigeria
  KE: ['en'], // Kenya
  GH: ['en'], // Ghana
  ZW: ['en'], // Zimbabwe
  JM: ['en'], // Jamaica
  TT: ['en'], // Trinidad and Tobago
  BB: ['en'], // Barbados
  BS: ['en'], // Bahamas
  BZ: ['en'], // Belize
  GY: ['en'], // Guyana
  MT: ['en'], // Malta
  PH: ['en'], // Philippines

  // French-speaking countries
  FR: ['fr'], // France
  BE: ['fr', 'en'], // Belgium
  CH: ['fr', 'en'], // Switzerland
  LU: ['fr', 'en'], // Luxembourg
  MC: ['fr'], // Monaco
  DZ: ['fr'], // Algeria
  MA: ['fr'], // Morocco
  TN: ['fr'], // Tunisia
  SN: ['fr'], // Senegal
  CI: ['fr'], // Côte d'Ivoire
  CM: ['fr', 'en'], // Cameroon
  CD: ['fr'], // Democratic Republic of the Congo
  CG: ['fr'], // Republic of the Congo
  GA: ['fr'], // Gabon
  ML: ['fr'], // Mali
  NE: ['fr'], // Niger
  BF: ['fr'], // Burkina Faso
  MG: ['fr'], // Madagascar
  BJ: ['fr'], // Benin
  TD: ['fr'], // Chad
  GN: ['fr'], // Guinea
  RW: ['fr', 'en'], // Rwanda
  BI: ['fr'], // Burundi
  TG: ['fr'], // Togo
  CF: ['fr'], // Central African Republic
  DJ: ['fr'], // Djibouti
  KM: ['fr'], // Comoros
  GQ: ['fr'], // Equatorial Guinea
  SC: ['fr', 'en'], // Seychelles
  VU: ['fr', 'en'], // Vanuatu
  PF: ['fr'], // French Polynesia
  NC: ['fr'], // New Caledonia
  HT: ['fr'], // Haiti

  // Spanish-speaking countries
  ES: ['es', 'ca', 'gl', 'eu'], // Spain
  MX: ['es'], // Mexico
  AR: ['es'], // Argentina
  CO: ['es'], // Colombia
  PE: ['es', 'qu', 'ay'], // Peru
  VE: ['es'], // Venezuela
  CL: ['es'], // Chile
  EC: ['es', 'qu'], // Ecuador
  GT: ['es'], // Guatemala
  CU: ['es'], // Cuba
  BO: ['es', 'qu', 'ay', 'gn'], // Bolivia
  DO: ['es'], // Dominican Republic
  HN: ['es'], // Honduras
  PY: ['gn', 'es'], // Paraguay
  SV: ['es'], // El Salvador
  NI: ['es'], // Nicaragua
  CR: ['es'], // Costa Rica
  PA: ['es'], // Panama
  UY: ['es'], // Uruguay
  PR: ['es', 'en'], // Puerto Rico
  // German-speaking countries
  DE: ['de'], // Germany
  AT: ['de'], // Austria
  LI: ['de'], // Liechtenstein

  // Italian-speaking countries
  IT: ['it'], // Italy
  SM: ['it'], // San Marino
  VA: ['it', 'la'], // Vatican City

  // Portuguese-speaking countries
  PT: ['pt'], // Portugal
  BR: ['pt'], // Brazil
  AO: ['pt'], // Angola
  MZ: ['pt'], // Mozambique
  GW: ['pt'], // Guinea-Bissau
  TL: ['pt', 'tet'], // East Timor
  CV: ['pt'], // Cape Verde
  ST: ['pt'], // São Tomé and Príncipe

  // Dutch-speaking countries
  NL: ['nl'], // Netherlands
  SR: ['nl'], // Suriname
  AW: ['nl', 'pap'], // Aruba
  CW: ['nl', 'pap'], // Curaçao

  // Nordic countries
  SE: ['sv'], // Sweden
  NO: ['no', 'nb', 'nn'], // Norway
  DK: ['da'], // Denmark
  FI: ['fi', 'sv'], // Finland
  IS: ['is'], // Iceland

  // Eastern European countries
  PL: ['pl'], // Poland
  CZ: ['cs'], // Czech Republic
  SK: ['sk'], // Slovakia
  HU: ['hu'], // Hungary
  RO: ['ro'], // Romania
  BG: ['bg'], // Bulgaria
  HR: ['hr'], // Croatia
  SI: ['sl'], // Slovenia
  LT: ['lt'], // Lithuania
  LV: ['lv'], // Latvia
  EE: ['et'], // Estonia
  RS: ['sr'], // Serbia
  BA: ['bs', 'hr', 'sr'], // Bosnia and Herzegovina
  MK: ['mk'], // North Macedonia
  AL: ['sq'], // Albania
  ME: ['sr'], // Montenegro
  XK: ['sq', 'sr'], // Kosovo

  // Russian-speaking countries
  RU: ['ru'], // Russia
  BY: ['be', 'ru'], // Belarus
  KZ: ['kk', 'ru'], // Kazakhstan
  KG: ['ky', 'ru'], // Kyrgyzstan
  TJ: ['tg', 'ru'], // Tajikistan
  TM: ['tk', 'ru'], // Turkmenistan
  UZ: ['uz', 'ru'], // Uzbekistan
  MD: ['ro', 'ru'], // Moldova
  UA: ['uk', 'ru'], // Ukraine

  // Middle Eastern countries
  TR: ['tr'], // Turkey
  IL: ['he', 'ar'], // Israel
  SA: ['ar'], // Saudi Arabia
  AE: ['ar'], // United Arab Emirates
  IQ: ['ar', 'ku'], // Iraq
  IR: ['fa', 'az', 'ku'], // Iran
  JO: ['ar'], // Jordan
  LB: ['ar', 'fr', 'en'], // Lebanon
  SY: ['ar', 'ku'], // Syria
  YE: ['ar'], // Yemen
  OM: ['ar'], // Oman
  KW: ['ar'], // Kuwait
  BH: ['ar'], // Bahrain
  QA: ['ar'], // Qatar
  PS: ['ar'], // Palestine
  CY: ['el', 'tr'], // Cyprus

  // Asian countries
  CN: ['zh'], // China
  JP: ['ja'], // Japan
  KR: ['ko'], // South Korea
  TH: ['th'], // Thailand
  VN: ['vi'], // Vietnam
  ID: ['id'], // Indonesia
  MY: ['ms', 'en', 'zh', 'ta'], // Malaysia
  MM: ['my'], // Myanmar
  KH: ['km'], // Cambodia
  LA: ['lo'], // Laos
  BD: ['bn'], // Bangladesh
  NP: ['ne'], // Nepal
  LK: ['si', 'ta', 'en'], // Sri Lanka
  MV: ['dv'], // Maldives
  BT: ['dz'], // Bhutan
  MN: ['mn'], // Mongolia
  TW: ['zh'], // Taiwan
  HK: ['zh', 'en'], // Hong Kong
  MO: ['zh', 'pt'], // Macau
  BN: ['ms'], // Brunei
  KP: ['ko'], // North Korea
  AF: ['ps', 'fa', 'uz', 'tk'], // Afghanistan

  // African countries (non-French/English)
  EG: ['ar'], // Egypt
  ET: ['am', 'om', 'ti', 'so'], // Ethiopia
  TZ: ['sw', 'en'], // Tanzania
  UG: ['en', 'sw'], // Uganda
  ZM: ['en'], // Zambia
  MW: ['en', 'ny'], // Malawi
  SO: ['so', 'ar'], // Somalia
  SD: ['ar', 'en'], // Sudan
  SS: ['en'], // South Sudan
  ER: ['ti', 'ar', 'en'], // Eritrea
  LY: ['ar'], // Libya
  MR: ['ar', 'fr'], // Mauritania
  GM: ['en'], // Gambia
  SL: ['en'], // Sierra Leone
  LR: ['en'], // Liberia
  BW: ['en'], // Botswana
  NA: ['en', 'af'], // Namibia
  LS: ['en', 'st'], // Lesotho
  SZ: ['en', 'ss'], // Eswatini
  MU: ['en', 'fr'], // Mauritius

  // Latin American countries
  GF: ['fr'], // French Guiana
  GP: ['fr'], // Guadeloupe
  MQ: ['fr'], // Martinique

  // Pacific countries
  FJ: ['en'], // Fiji
  PG: ['en'], // Papua New Guinea
  SB: ['en'], // Solomon Islands
  WS: ['en'], // Samoa
  TO: ['en'], // Tonga
  KI: ['en'], // Kiribati
  TV: ['en'], // Tuvalu
  NR: ['en'], // Nauru
  PW: ['en'], // Palau
  MH: ['en'], // Marshall Islands
  FM: ['en'], // Micronesia

  // Caribbean
  LC: ['en'], // Saint Lucia
  VC: ['en'], // Saint Vincent and the Grenadines
  GD: ['en'], // Grenada
  AG: ['en'], // Antigua and Barbuda
  DM: ['en'], // Dominica
  KN: ['en'], // Saint Kitts and Nevis

  // Other
  GR: ['el'], // Greece
  AM: ['hy'], // Armenia
  AZ: ['az'], // Azerbaijan
  GE: ['ka'], // Georgia
};

/**
 * Gets the preferred language for a given country code.
 * Returns the first (most commonly spoken) language for that country.
 * 
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US', 'FR')
 * @returns ISO 639-1 language code (e.g., 'en', 'fr') or undefined if country not found
 */
export function getLanguageForCountry(countryCode: string): string | undefined {
  const upperCode = countryCode.toUpperCase();
  const languages = COUNTRY_LANGUAGE_MAP[upperCode];
  return languages?.[0];
}

/**
 * Gets all supported languages for a given country code.
 * 
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @returns Array of ISO 639-1 language codes
 */
export function getLanguagesForCountry(countryCode: string): string[] {
  const upperCode = countryCode.toUpperCase();
  return COUNTRY_LANGUAGE_MAP[upperCode] || [];
}
